import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import { Tenant } from '@/domain/model/Tenant'

export interface BlogPostRow {
  id: string
  siteId: string
  title: string
  slug: string
  excerpt: string | null
  content: unknown[]
  coverImage: string | null
  published: boolean
  publishedAt: string | null
  tags: string[]
  authorId: string | null
  createdAt: string
  updatedAt: string
}

function mapRow(row: Record<string, unknown>): BlogPostRow {
  return {
    id: row['id'] as string,
    siteId: row['site_id'] as string,
    title: row['title'] as string,
    slug: row['slug'] as string,
    excerpt: (row['excerpt'] as string | null) ?? null,
    content: (row['content'] as unknown[]) ?? [],
    coverImage: (row['cover_image'] as string | null) ?? null,
    published: row['published'] as boolean,
    publishedAt: (row['published_at'] as string | null) ?? null,
    tags: (row['tags'] as string[]) ?? [],
    authorId: (row['author_id'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  }
}

export function useBlogPosts(siteId: string) {
  return useQuery<BlogPostRow[]>({
    queryKey: ['blog-posts', siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('site_id', siteId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []).map(mapRow)
    },
    enabled: !!siteId,
  })
}

export interface BlogPostInput {
  title: string
  slug: string
  excerpt: string
  content: string   // raw HTML — stored as [{type:'text',props:{content,alignment:'left'},...}]
  coverImage: string
  tags: string      // comma-separated
  published: boolean
}

function buildContent(html: string) {
  if (!html.trim()) return []
  return [{ id: 'c0', type: 'text', props: { content: html, alignment: 'left' }, settings: {}, orderIndex: 0, pageId: '', parentId: null }]
}

export function useCreateBlogPost(siteId: string) {
  const qc = useQueryClient()
  const { user, tenant } = useAuthStore()

  return useMutation({
    mutationFn: async (input: BlogPostInput) => {
      if (tenant) {
        const { count } = await supabase
          .from('blog_posts')
          .select('id', { count: 'exact', head: true })
          .eq('site_id', siteId)
        const t = Tenant.reconstitute({ ...tenant, createdAt: new Date(tenant.createdAt), updatedAt: new Date(tenant.updatedAt) })
        if (!t.canCreateBlogPost(count ?? 0)) {
          throw new Error(`Your ${tenant.plan} plan allows up to 5 blog posts per site. Upgrade to create more.`)
        }
      }

      const { error } = await supabase.from('blog_posts').insert({
        site_id: siteId,
        author_id: user?.id ?? null,
        title: input.title,
        slug: input.slug,
        excerpt: input.excerpt || null,
        content: buildContent(input.content),
        cover_image: input.coverImage || null,
        tags: input.tags ? input.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        published: input.published,
        published_at: input.published ? new Date().toISOString() : null,
        meta: {},
      })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog-posts', siteId] }),
  })
}

export function useUpdateBlogPost(siteId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: BlogPostInput }) => {
      const { error } = await supabase.from('blog_posts').update({
        title: input.title,
        slug: input.slug,
        excerpt: input.excerpt || null,
        content: buildContent(input.content),
        cover_image: input.coverImage || null,
        tags: input.tags ? input.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        published: input.published,
        published_at: input.published ? new Date().toISOString() : null,
      }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog-posts', siteId] }),
  })
}

export function useDeleteBlogPost(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('blog_posts').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog-posts', siteId] }),
  })
}

export function useToggleBlogPostPublished(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, publish }: { id: string; publish: boolean }) => {
      const { error } = await supabase.from('blog_posts').update({
        published: publish,
        published_at: publish ? new Date().toISOString() : null,
      }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog-posts', siteId] }),
  })
}
